import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines, ChapterLabel, EASE } from "@/components/motion";
import Marquee from "@/components/Marquee";
import ImageFrame from "@/components/ImageFrame";
import ButtonLink from "@/components/ButtonLink";
import { IMAGES, MARQUEE_ITEMS, AREAS, BOOKING_URL } from "@/data/content";

const Hero = () => (
  <section data-testid="homepage-hero-section" className="relative overflow-hidden">
    <div className="mx-auto grid min-h-[92vh] max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-24">
      <div className="lg:col-span-7">
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-muted sm:text-xs sm:tracking-[0.3em]"
        >
          Neurofeedback × Hjernetræning × Regulering
        </motion.p>
        <h1
          data-testid="hero-main-heading"
          className="mt-6 font-serif text-[clamp(2.9rem,7.5vw,6.6rem)] leading-[1.02] tracking-tight text-charcoal"
        >
          <MaskedLines
            delay={0.25}
            lines={[
              "Hjernen er",
              "ikke statisk.",
              <span key="t">
                Den kan <span className="font-light italic">trænes.</span>
              </span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
          className="mt-8 max-w-xl text-lg font-light leading-relaxed text-graphite"
        >
          Hjernen tilpasser sig hele livet. Hos Center for Hjerneregulering trænes hjernens evne til
          selvregulering med ILF Neurofeedback. Det sker gennem præcis feedback om hjernens egen aktivitet
          i realtid.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <ButtonLink to={BOOKING_URL || "/kontakt"} testId="hero-book-consultation-btn">
            Book en indledende samtale
          </ButtonLink>
          <ButtonLink to="/neurofeedback" variant="outline" testId="hero-explore-methods-btn" arrow>
            Udforsk metoden
          </ButtonLink>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 grid grid-cols-3 gap-6 border-t border-hairline pt-8"
        >
          {[
            ["01", "ILF Neurofeedback"],
            ["02", "Feedback i realtid"],
            ["03", "Individuelt forløb"],
          ].map(([n, t]) => (
            <div key={n}>
              <p className="font-mono text-[11px] tracking-[0.2em] text-muted">{n}</p>
              <p className="mt-2 text-sm text-graphite">{t}</p>
            </div>
          ))}
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
        >
          <ImageFrame
            src={IMAGES.hero}
            alt="Person i stol under neurofeedback-session set bagfra"
            caption="Fig. 01 — Neurofeedback-session"
            testId="hero-editorial-portrait-img"
            ratio="aspect-[3/4]"
            tone="none"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

const ChapterSelfRegulation = () => (
  <section data-testid="section-chapter-selvregulering" className="border-t border-hairline">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
      <div className="lg:col-span-6">
        <Reveal>
          <ChapterLabel number="01" label="Hjernens selvregulering" />
        </Reveal>
        <h2 className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
          <MaskedLines mode="view" lines={["Et system i", "konstant balance"]} />
        </h2>
        <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
          <p>
            Hjernen skifter hele dagen mellem tilstande: vågen og opmærksom, rolig og nærværende, træt og
            sovende. Evnen til at skifte fleksibelt, at skrue op og ned for aktiviteten præcis når
            situationen kræver det, kalder vi selvregulering.
          </p>
          <p>
            Når selvreguleringen fungerer, mærker vi det som ro i kroppen, fokus i opgaven og søvn, der
            kommer, når den skal. Når den er presset af stress, overstimulering eller udfordringer med
            opmærksomhed, kan hjernen hænge fast i tilstande, der ikke passer til hverdagen.
          </p>
          <p>Selvregulering er ikke en medfødt egenskab, man enten har eller ikke har. Det er en evne, der kan trænes.</p>
        </Reveal>
        <Reveal delay={0.25} className="mt-8">
          <Link
            to="/hjerneregulering"
            data-testid="home-link-hjerneregulering"
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-charcoal"
          >
            <span className="border-b border-charcoal pb-1">Læs om hjerneregulering</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ChapterNeurofeedback = () => (
  <section data-testid="section-chapter-neurofeedback" className="bg-obsidian text-[#F5F5F0]">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-32">
      <div className="order-2 lg:order-1 lg:col-span-5">
        <Reveal delay={0.1}>
          <ImageFrame
            src={IMAGES.method}
            alt="Computer viser neurofeedback-software under træning"
            caption="Fig. 03 — Sensorer måler hjernens aktivitet"
            testId="home-neurofeedback-img"
            tone="soft"
            dark
          />
        </Reveal>
      </div>
      <div className="order-1 lg:order-2 lg:col-span-7">
        <Reveal>
          <ChapterLabel number="02" label="Neurofeedback" dark />
        </Reveal>
        <h2 className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLines
            mode="view"
            lines={[
              "Et spejl til",
              <span key="h" className="italic font-light text-[#A3A299]">
                hjernen.
              </span>,
            ]}
          />
        </h2>
        <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-[#A3A299]">
          <p>
            Neurofeedback er træning, ikke behandling. Center for Hjerneregulering arbejder med ILF
            Neurofeedback (Infra-Low Frequency) efter Othmer-metoden. Sensorer på hovedbunden registrerer
            hjernens elektriske aktivitet, som omsættes til feedback gennem lyd eller billede.
          </p>
          <p>
            Feedbacken ændrer sig løbende i takt med den aktivitet, der registreres, og indgår i træningen
            af hjernens selvregulering. Sensorerne måler kun. De tilfører ikke elektrisk stimulation, og
            metoden involverer hverken medicin eller indgreb.
          </p>
        </Reveal>
        <Reveal delay={0.25} className="mt-10">
          <ButtonLink to="/neurofeedback" variant="outlineLight" testId="home-neurofeedback-link" arrow>
            Udforsk neurofeedback
          </ButtonLink>
        </Reveal>
      </div>
    </div>
  </section>
);

const ChapterWhy = () => (
  <section data-testid="section-chapter-hvorfor" className="border-b border-hairline bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
      <Reveal>
        <ChapterLabel number="03" label="Hvorfor træne hjernen?" />
      </Reveal>
      <h2 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-charcoal sm:text-6xl lg:text-7xl">
        <MaskedLines
          mode="view"
          delay={0.1}
          lines={[
            "Vi træner kroppen.",
            "Vi måler søvnen.",
            "Vi tæller skridtene.",
            <span key="m" className="font-light italic text-graphite">
              Men hjernen forventes at klare sig selv.
            </span>,
          ]}
        />
      </h2>
      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <Reveal delay={0.1} className="lg:col-span-5">
          <ImageFrame
            src={IMAGES.brainArt}
            alt="Person i neurofeedback-stol med høretelefoner og tæppe under træning"
            testId="home-brain-art-img"
            ratio="aspect-square"
            tone="none"
          />
        </Reveal>
        <Reveal delay={0.2} className="flex items-end lg:col-span-5 lg:col-start-8">
          <p className="text-lg font-light leading-relaxed text-graphite">
            Neuroplasticitet er hjernens evne til at forandre sig gennem hele livet. Hver gentagelse,
            hver oplevelse og hver tilbagemelding former de forbindelser, hjernen bruger i morgen.
            Hjernetræning handler om at bruge den egenskab bevidst og give hjernen den feedback, den
            sjældent får på egen hånd.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ChapterAudiences = () => (
  <section data-testid="section-chapter-maalgrupper">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Reveal>
        <ChapterLabel number="04" label="Hvem" />
      </Reveal>
      <h2 className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
        <MaskedLines mode="view" lines={["Hvem kan have glæde", "af hjernetræning?"]} />
      </h2>
      <Reveal delay={0.15} className="mt-6 max-w-2xl">
        <p className="text-base leading-relaxed text-graphite">
          Hjernetræning er for børn og voksne, for dem der møder udfordringer, og for dem der vil tage
          aktivt vare på hjernen.
        </p>
      </Reveal>
      <div className="mt-12">
        {AREAS.map((a, i) => (
          <Reveal key={a.title} delay={Math.min(i * 0.04, 0.2)}>
            <div className="group grid grid-cols-[2.5rem_1fr] gap-x-4 gap-y-2 border-t border-hairline py-7 sm:grid-cols-12 sm:items-baseline sm:gap-6">
              <span className="font-mono text-[11px] tracking-[0.2em] text-muted sm:col-span-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-serif text-2xl tracking-tight text-charcoal transition-transform duration-300 group-hover:translate-x-1 sm:col-span-4 sm:text-3xl">
                {a.title}
              </h3>
              <p className="col-start-2 text-sm leading-relaxed text-graphite sm:col-span-6 sm:col-start-7">
                {a.desc}
              </p>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-hairline" />
      </div>
      <Reveal delay={0.1} className="mt-8">
        <p className="max-w-3xl text-sm leading-relaxed text-muted">
          Neurofeedback er en træningsmetode. Center for Hjerneregulering stiller ikke diagnoser og lover
          ikke bestemte resultater. Responsen varierer fra person til person.
        </p>
      </Reveal>
    </div>
  </section>
);
const TESTIMONIALS = [
  {
    quote:
      "Jeg kan mærke, at mine vredesudbrud er forsvundet. Jeg bliver ikke længere rasende i trafikken eller over mine kollegaer.",
    source: "Klient, 52 år",
  },
  {
    quote:
      "Hun finder meget hurtigere tilbage til ro nu og tager selv pauser, i stedet for at køre sig selv op eller helt ned. De har også bemærket forandringen i skolen.",
    source: "Forælder til barn, 7 år",
  },
  {
    quote:
      "Jeg føler, at jeg kan se mig selv lidt udefra og har fået tid til at vælge, hvordan jeg vil reagere. Situationerne løber ikke længere afsted med mig.",
    source: "Klient, 47 år",
  },
  {
    quote:
      "Vi oplever mere øjenkontakt, bedre søvn og at han ikke bliver belastet af høje lyde og mange stimuli på samme måde som før. Skolen oplever også en meget gladere dreng.",
    source: "Forælder til barn, 10 år",
  },
];

const ChapterExperiences = () => (
  <section data-testid="section-chapter-erfaringer" className="border-t border-hairline bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Reveal>
        <ChapterLabel number="05" label="Erfaringer fra forløb" />
      </Reveal>
      <h2 className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
        <MaskedLines mode="view" lines={["Når forandringen", "mærkes i hverdagen"]} />
      </h2>
      <div className="mt-12 grid grid-cols-1 gap-px border border-sand bg-sand sm:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.source} delay={i * 0.06} className="bg-canvas">
            <figure
              data-testid={`testimonial-${i + 1}`}
              className="flex h-full flex-col justify-between p-8 transition-colors duration-300 hover:bg-subtle lg:p-10"
            >
              <blockquote className="font-serif text-xl font-light leading-relaxed tracking-tight text-charcoal sm:text-2xl">
                {t.quote}
              </blockquote>
              <figcaption className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                {String(i + 1).padStart(2, "0")} · {t.source}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1} className="mt-8">
        <p className="max-w-3xl text-sm leading-relaxed text-muted">
          Citaterne er individuelle klienters og forældres egne oplevelser af deres forløb. De er ikke
          dokumentation for en bestemt effekt, og responsen på træning varierer fra person til person.
        </p>
      </Reveal>
    </div>
  </section>
);


const Forside = () => (
  <main data-testid="homepage">
    <Hero />
    <Marquee items={MARQUEE_ITEMS} />
    <ChapterSelfRegulation />
    <ChapterNeurofeedback />
    <ChapterWhy />
    <ChapterAudiences />
    <ChapterExperiences />
  </main>
);

export default Forside;
